import { useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CAMADA } from '@/lib/camadas';
import { cn } from '@/lib/cn';

/**
 * Dialog (Modal) — overlay com card central.
 *
 * Uso:
 *   const [open, setOpen] = useState(false);
 *   <Dialog open={open} onOpenChange={setOpen}>
 *     <DialogHeader>
 *       <DialogTitle>Confirmar exclusão</DialogTitle>
 *       <DialogDescription>Essa ação não pode ser desfeita.</DialogDescription>
 *     </DialogHeader>
 *     <DialogContent>...</DialogContent>
 *     <DialogFooter>
 *       <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
 *       <Button variant="destructive">Excluir</Button>
 *     </DialogFooter>
 *   </Dialog>
 *
 * Acessibilidade:
 *   - ESC fecha o modal
 *   - Click no overlay fecha
 *   - role="dialog" + aria-modal
 *
 * Limitações conhecidas (R0): sem focus trap completo. Aceitável pra R0;
 * R1+ podemos adicionar @radix-ui/react-dialog se precisar.
 */

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  /** Largura máxima — default 'md' (560px) */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /**
   * Porta opcional (Agenda da OS, 25/10/2026: filtros num painel lateral que abre pelo ícone): `'direita'` encosta o
   * painel na borda direita, na altura toda. Sem ela, nada muda para quem já usava o diálogo centralizado.
   */
  lado?: 'centro' | 'direita';
  /** Nome do diálogo para leitor de tela (o painel lateral não tem o título visível de todos). */
  ariaLabel?: string;
}

const SIZE_CLASSES = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Dialog({ open, onOpenChange, children, size = 'md', lado = 'centro', ariaLabel }: DialogProps) {
  const lateral = lado === 'direita';
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, handleEscape]);

  if (!open) return null;

  return createPortal(
    <div
      // N-1: a camada vem da escada única (`src/lib/camadas.ts`), não de um
      // número solto. Era `z-50` — o mesmo do Toast, que por isso ficava atrás.
      style={{ zIndex: CAMADA.DIALOGO }}
      className={cn(
        'fixed inset-0 flex animate-fade-in',
        lateral ? 'items-stretch justify-end' : 'items-center justify-center p-4 max-md:p-0',
      )}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-ink-1/40 dark:bg-ink-dark-1/30 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      {/* Content */}
      <div
        className={cn(
          lateral
            ? // Painel lateral: altura toda, sem cantos do lado da borda; no celular, a tela inteira (mesma régua do M5).
              'relative h-full w-full max-w-sm overflow-y-auto bg-paper-surface dark:bg-paper-dark-surface shadow-card-hover max-md:max-w-none'
            : cn(
                'relative w-full max-h-[90vh] overflow-y-auto bg-paper-surface dark:bg-paper-dark-surface rounded-2xl shadow-card-hover animate-slide-up',
                SIZE_CLASSES[size],
                // MOBILE-COMPLETO (M5): no celular o diálogo ocupa a tela inteira — o cartão centralizado deixava
                // o confirmar abaixo da dobra e o fundo rolando atrás. No computador, nada muda.
                'max-md:h-full max-md:max-h-full max-md:max-w-none max-md:rounded-none',
              ),
        )}
      >
        {/* No celular, o "fechar" fica fixo no topo (alcançável sem rolar). No computador o diálogo segue com o
            fechar que cada um já tinha — este botão é `md:hidden`. */}
        <div className="sticky top-0 z-10 flex justify-end bg-paper-surface md:hidden dark:bg-paper-dark-surface">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Fechar esta janela"
            className="inline-flex h-11 w-11 items-center justify-center text-2xl leading-none text-ink-2 dark:text-ink-dark-2"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

export function DialogHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('px-6 pt-6 pb-3', className)}>{children}</div>;
}

export function DialogTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h2 className={cn('font-display text-2xl text-ink-1 dark:text-ink-dark-1 tracking-tight', className)}>
      {children}
    </h2>
  );
}

export function DialogDescription({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('text-sm text-ink-2 dark:text-ink-dark-2 mt-1', className)}>{children}</p>;
}

export function DialogContent({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('px-6 py-3', className)}>{children}</div>;
}

/**
 * ALTURA (05/08/2026): o footer é STICKY dentro do painel rolável — num
 * notebook 1366×768 o modal "Nova proposta" abria com Cancelar/Criar proposta
 * ABAIXO da dobra (medido com Playwright em 1366×625): parecia um formulário
 * sem botão nenhum. As ações de um modal nunca podem depender de o usuário
 * adivinhar que existe rolagem. O fundo opaco cobre o conteúdo que passa por
 * baixo durante a rolagem.
 */
export function DialogFooter({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'sticky bottom-0 px-6 py-4 flex items-center justify-end gap-2 border-t border-line-soft dark:border-line-soft-dark bg-paper-surface dark:bg-paper-dark-surface rounded-b-2xl max-md:rounded-none',
        className,
      )}
    >
      {children}
    </div>
  );
}
